import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';

interface StatsPanelProps {
  stats: {
    strength: number;
    intelligence: number;
    agility: number;
    vitality: number;
    sense: number;
  };
  unallocatedPoints?: number;
  onAllocateClick?: () => void;
}

const StatsPanel: React.FC<StatsPanelProps> = ({ stats, unallocatedPoints = 0, onAllocateClick }) => {
  const { t } = useLanguage();
  const { profile } = useUser();
  
  // Get passive boosts to show them
  const passiveBoosts = profile?.passiveStatBoosts || { strength: 0, intelligence: 0, agility: 0, vitality: 0, sense: 0 };
  const baseStats = profile?.stats || stats;
  
  const statConfigs = [
    { 
      name: t.stats.strength, 
      key: 'STR',
      statKey: 'strength' as const,
      value: stats.strength, 
      barColor: 'from-red-500 to-rose-400',
      glowColor: 'rgba(239, 68, 68, 0.4)',
      iconPath: '/icons/icon-stat-strength.png'
    },
    { 
      name: t.stats.intelligence, 
      key: 'INT',
      statKey: 'intelligence' as const,
      value: stats.intelligence, 
      barColor: 'from-blue-500 to-cyan-400',
      glowColor: 'rgba(59, 130, 246, 0.4)',
      iconPath: '/icons/icon-stat-intelligence.png'
    },
    { 
      name: t.stats.agility, 
      key: 'AGI',
      statKey: 'agility' as const,
      value: stats.agility, 
      barColor: 'from-green-500 to-emerald-400',
      glowColor: 'rgba(16, 185, 129, 0.4)',
      iconPath: '/icons/icon-stat-agility.png'
    },
    { 
      name: t.stats.vitality, 
      key: 'VIT',
      statKey: 'vitality' as const,
      value: stats.vitality, 
      barColor: 'from-orange-500 to-amber-400',
      glowColor: 'rgba(249, 115, 22, 0.4)',
      iconPath: '/icons/icon-stat-vitality.png'
    },
    { 
      name: t.stats.sense, 
      key: 'SNS',
      statKey: 'sense' as const,
      value: stats.sense, 
      barColor: 'from-purple-500 to-pink-400',
      glowColor: 'rgba(168, 85, 247, 0.4)',
      iconPath: '/icons/icon-stat-sense.png'
    }
  ];

  return (
    <div 
      className="relative overflow-hidden bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
      style={{
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
      }}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-system-blue/30">
          <div className="flex items-center gap-2">
            <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-system-blue to-blue-400 rounded-full shadow-lg shadow-system-blue/50"></div>
            <h3 className="font-orbitron text-xs sm:text-sm tracking-wider text-system-blue uppercase font-bold">
              ［ {t.stats.title} ］
            </h3>
          </div>
          {onAllocateClick && unallocatedPoints > 0 && (
            <button
              onClick={onAllocateClick}
              className="px-2 py-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full font-orbitron font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 hover:shadow-lg hover:shadow-yellow-500/20 transition-all"
            >
              <img 
                src="/icons/icon-statpoints.png" 
                alt="Stat Points"
                className="w-3 h-3 object-contain"
                style={{ filter: 'drop-shadow(0 0 4px rgba(252, 211, 77, 0.8))' }}
              />
              <span className="text-yellow-400">
                ＋{unallocatedPoints}
              </span>
            </button>
          )}
        </div>
        
        {/* Stats List */}
        <div className="space-y-2">
          {statConfigs.map((stat, index) => (
            <div 
              key={stat.name}
              className="relative group"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Stat Row */}
              <div className="relative flex items-center py-1.5 sm:py-2 px-2 sm:px-3 hover:bg-system-blue/5 rounded-lg transition-all">
                {/* Icon */}
                <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center mr-2 sm:mr-3">
                  <img 
                    src={stat.iconPath} 
                    alt={stat.name}
                    className="w-6 h-6 sm:w-7 sm:h-7 object-contain transition-all group-hover:scale-110"
                    style={{ 
                      filter: `drop-shadow(0 0 6px ${stat.glowColor})`
                    }}
                  />
                </div>

                {/* Stat Name & Value */}
                <div className="flex-1 flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-xs font-orbitron text-gray-400 tracking-wider uppercase leading-tight">
                      {stat.key}
                    </span>
                    <span className="text-xs sm:text-sm font-orbitron text-white tracking-wider uppercase font-medium leading-tight mt-0.5">
                      {stat.name}
                    </span>
                  </div>
                  
                  {/* Value */}
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xl sm:text-2xl font-black font-orbitron tabular-nums tracking-tight" 
                      style={{ 
                        color: '#fff',
                        textShadow: `0 0 15px ${stat.glowColor}`
                      }}>
                      {stat.value}
                    </span>
                    {/* Show passive boost indicator if any */}
                    {(passiveBoosts[stat.statKey] || 0) > 0 && (
                      <span className="text-xs font-rajdhani text-green-400 flex items-center gap-1" title="Passive Skill Boost">
                        <span className="text-sm">✨</span>
                        <span>+{((passiveBoosts[stat.statKey] || 0) * 100).toFixed(1)}%</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="relative h-1.5 bg-black/30 rounded-full overflow-hidden mx-2 mb-1">
                <div 
                  className={`h-full bg-gradient-to-r ${stat.barColor} rounded-full transition-all duration-1000 ease-out relative`}
                  style={{ 
                    width: `${Math.min(stat.value * 10, 100)}%`,
                    boxShadow: `0 0 8px ${stat.glowColor}`
                  }}
                >
                  {/* Pulse effect */}
                  <div className="absolute right-0 top-0 bottom-0 w-2 bg-white/30 blur-sm rounded-r-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Unallocated Points Button */}
        {onAllocateClick && unallocatedPoints > 0 && (
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-yellow-500/30">
            <button
              onClick={onAllocateClick}
              className="w-full py-2 sm:py-3 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/40 rounded-lg font-orbitron font-bold text-xs sm:text-sm tracking-wider uppercase transition-all hover:shadow-lg hover:shadow-yellow-500/20 hover:scale-[1.02]"
            >
              <span className="flex items-center justify-center gap-2">
                <span className="text-base sm:text-lg text-yellow-400">
                  ⚡
                </span>
                <span>
                  {`［ ${t.stats.allocate.toUpperCase()} ${unallocatedPoints} ${t.common.level.toUpperCase()} ］`}
                </span>
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsPanel;

